import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { toast } from 'sonner';

const STORAGE_KEY = 'helmut_inquiry_list';

const InquiryListContext = createContext();

export function InquiryListProvider({ children }) {
  const [items, setItems] = useState([]);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);

  // Load from localStorage on mount
  useEffect(() => {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (stored) {
        setItems(JSON.parse(stored));
      }
    } catch (e) {
      console.error('Failed to load inquiry list:', e);
    }
  }, []);

  // Save to localStorage whenever items change
  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(items));
    } catch (e) {
      console.error('Failed to save inquiry list:', e);
    }
  }, [items]);

  const addItem = useCallback((product, quantity = 1) => {
    setItems(prev => {
      // Check if already in list
      const existingIndex = prev.findIndex(item => item.id === product.id);
      const maxQty = product.quantity || 99;
      
      if (existingIndex >= 0) {
        // Update quantity (respect max)
        const updated = [...prev];
        const newQty = Math.min(updated[existingIndex].quantity + quantity, maxQty);
        updated[existingIndex] = {
          ...updated[existingIndex],
          quantity: newQty
        };
        toast.success('Quantity updated', {
          description: `${product.name_de || product.name_en} is now ${updated[existingIndex].quantity}×`
        });
        return updated;
      }
      
      // Add new item
      const newItem = {
        id: product.id,
        slug: product.slug,
        name_de: product.name_de,
        name_en: product.name_en,
        name_it: product.name_it,
        price: product.price,
        currency: product.currency || 'EUR',
        sku: product.sku,
        image_url: product.image_url,
        maxQuantity: maxQty,
        quantity: Math.min(quantity, maxQty),
        addedAt: new Date().toISOString()
      };
      
      const newItems = [...prev, newItem];
      toast.success('Added to inquiry list', {
        description: `${newItems.length} ${newItems.length === 1 ? 'item' : 'items'} in your list`
      });
      return newItems;
    });
  }, []);

  const removeItem = useCallback((productId) => {
    setItems(prev => prev.filter(item => item.id !== productId));
  }, []);

  const updateQuantity = useCallback((productId, quantity) => {
    if (quantity < 1) return;
    setItems(prev => prev.map(item => {
      if (item.id === productId) {
        const maxQty = item.maxQuantity || 99;
        return { ...item, quantity: Math.min(quantity, maxQty) };
      }
      return item;
    }));
  }, []);

  const clearList = useCallback(() => {
    setItems([]);
  }, []);

  const isInList = useCallback((productId) => {
    return items.some(item => item.id === productId);
  }, [items]);

  const openDrawer = useCallback(() => setIsDrawerOpen(true), []);
  const closeDrawer = useCallback(() => setIsDrawerOpen(false), []);

  return (
    <InquiryListContext.Provider value={{
      items,
      itemCount: items.length,
      addItem,
      removeItem,
      updateQuantity,
      clearList,
      isInList,
      isDrawerOpen,
      openDrawer,
      closeDrawer
    }}>
      {children}
    </InquiryListContext.Provider>
  );
}

export function useInquiryList() {
  const context = useContext(InquiryListContext);
  if (!context) {
    throw new Error('useInquiryList must be used within an InquiryListProvider');
  }
  return context;
}
